import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Target, Eye, Users, Lightbulb, 
  TrendingUp, Shield, ArrowLeft, Brain
} from 'lucide-react';
import MarketingHeader from '../components/MarketingHeader';
import SEOHead from '../components/SEOHead';

const About = () => {
  // SEO structured data for about page
  const aboutStructuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About ScopeTheMarket",
    "description": "ScopeTheMarket is an AI-powered S&P 500 stock anomaly detection platform helping retail investors spot unusual market activity.",
    "url": "https://scopethemarket.com/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "ScopeTheMarket",
      "description": "AI-powered stock market intelligence platform",
      "url": "https://scopethemarket.com"
    }
  };

  const values = [
    {
      icon: Eye,
      title: 'Clarity',
      description: 'We believe complex stock market data should be easy to understand. No jargon, just insights.'
    },
    {
      icon: Shield,
      title: 'Reliability',
      description: 'Our S&P 500 stock scanning algorithms run continuously, ensuring you never miss an important market move.'
    },
    {
      icon: Users,
      title: 'Accessibility',
      description: 'Built for everyone - from first-time stock investors to seasoned professionals.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'Leveraging cutting-edge AI to transform raw stock data into actionable intelligence.'
    }
  ];

  const audiences = [
    {
      icon: Users,
      title: 'Retail Stock Investors',
      description: 'Individual investors looking to stay informed about unusual S&P 500 stock activity without spending hours on research.'
    },
    {
      icon: TrendingUp,
      title: 'Stock Market Beginners',
      description: 'New to stock investing? Our plain-English explanations help you understand market movements without the learning curve.'
    },
    {
      icon: Brain,
      title: 'Stock Researchers',
      description: 'Analysts and researchers who need quick access to S&P 500 stock anomalies and AI-powered insights for their work.'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0F1A' }}>
      <SEOHead
        title="About ScopeTheMarket | AI Stock Anomaly Detection Platform"
        description="ScopeTheMarket is an AI-powered S&P 500 stock anomaly detection platform. Learn about our mission to help retail investors spot unusual market activity."
        canonical="https://scopethemarket.com/about"
        ogTitle="About ScopeTheMarket – Our Mission"
        ogDescription="Helping retail investors spot unusual S&P 500 stock activity with AI-powered anomaly detection."
        structuredData={aboutStructuredData}
      />
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-10"
          style={{
            background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)',
            top: '-200px',
            right: '-200px'
          }}
        />
        <div 
          className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-10"
          style={{
            background: 'radial-gradient(circle, #7C3AED 0%, transparent 70%)',
            bottom: '-200px',
            left: '-200px'
          }}
        />
      </div>

      {/* Auth-aware Marketing Header */}
      <MarketingHeader 
        navLinks={[
          { to: '/pricing', label: 'Pricing' },
          { to: '/about', label: 'About' },
          { to: '/blog', label: 'Blog' }
        ]}
      />

      {/* Main Content */}
      <div className="relative z-10 py-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back link */}
          <Link 
            to="/"
            className="inline-flex items-center gap-2 mb-8 text-sm transition-colors hover:text-white"
            style={{ color: '#64748B' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Hero Section */}
          <div className="mb-20">
            <h1 
              className="text-4xl sm:text-5xl font-bold mb-6"
              style={{ color: '#F8FAFC' }}
            >
              Simplifying{' '}
              <span style={{ 
                background: 'linear-gradient(135deg, #3B82F6 0%, #00F5FF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                S&P 500 Stock Intelligence
              </span>
            </h1>
            <p className="text-lg leading-relaxed" style={{ color: '#94A3B8' }}>
              We built ScopeTheMarket with a simple mission: make S&P 500 stock market intelligence accessible to everyone. 
              No complex charts, no confusing jargon - just clear, actionable stock insights powered by artificial intelligence.
            </p>
          </div>

          {/* Mission Section */}
          <section className="mb-20">
            <div 
              className="p-8 md:p-12 rounded-2xl"
              style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(59, 130, 246, 0.15)',
                backdropFilter: 'blur(12px)'
              }}
            >
              <div className="flex items-start gap-4 mb-6">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ 
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.2)'
                  }}
                >
                  <Target className="w-6 h-6" style={{ color: '#3B82F6' }} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-4" style={{ color: '#F8FAFC' }}>Our Mission</h2>
                  <p className="leading-relaxed" style={{ color: '#94A3B8' }}>
                    The stock market generates massive amounts of data every second. For most people, making sense 
                    of this information is overwhelming. We're changing that by using AI to detect what matters 
                    and explain it in plain English - so you can make informed stock decisions without becoming a data scientist.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Why Anomaly Detection Section */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#F8FAFC' }}>
              Why S&P 500 Stock Anomaly Detection Matters
            </h2>
            <div className="space-y-4">
              <p className="leading-relaxed" style={{ color: '#94A3B8' }}>
                Stock markets are full of noise. Every day, thousands of stocks move up and down for countless reasons. 
                But sometimes, a move isn't just random - it's a signal. Unusual trading volume, unexpected price 
                spikes, or abnormal patterns can indicate something significant is happening with a stock.
              </p>
              <p className="leading-relaxed" style={{ color: '#94A3B8' }}>
                Our S&P 500 stock anomaly detection system continuously monitors the market, identifying these statistical 
                outliers before they become mainstream news. Combined with AI-powered analysis, we help you 
                understand not just <em>what</em> is happening, but <em>why</em> it matters.
              </p>
            </div>
          </section>

          {/* Who This Is For */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-8" style={{ color: '#F8FAFC' }}>
              Who This Is For
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {audiences.map((audience, index) => (
                <div 
                  key={index}
                  className="p-6 rounded-2xl"
                  style={{
                    background: 'rgba(15, 23, 42, 0.4)',
                    border: '1px solid rgba(148, 163, 184, 0.1)'
                  }}
                  data-testid={`audience-card-${index + 1}`}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ 
                      background: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.15)'
                    }}
                  >
                    <audience.icon className="w-6 h-6" style={{ color: '#3B82F6' }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#F8FAFC' }}>
                    {audience.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#94A3B8' }}>
                    {audience.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Our Values */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-8" style={{ color: '#F8FAFC' }}>
              Our Values
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {values.map((value, index) => (
                <div 
                  key={index}
                  className="p-6 rounded-2xl flex items-start gap-4"
                  style={{
                    background: 'rgba(15, 23, 42, 0.4)',
                    border: '1px solid rgba(148, 163, 184, 0.1)'
                  }}
                  data-testid={`value-card-${index + 1}`}
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ 
                      background: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.15)'
                    }}
                  >
                    <value.icon className="w-5 h-5" style={{ color: '#3B82F6' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#F8FAFC' }}>
                      {value.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: '#94A3B8' }}>
                      {value.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Vision Section */}
          <section className="mb-20">
            <div 
              className="p-8 md:p-12 rounded-2xl text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}
            >
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#F8FAFC' }}>
                Our Vision
              </h2>
              <p className="text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: '#CBD5E1' }}>
                We envision a world where every investor, regardless of experience or resources, has access 
                to institutional-grade market intelligence. AI-powered financial clarity for everyone.
              </p>
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#F8FAFC' }}>
              Ready to get started?
            </h2>
            <p className="mb-8" style={{ color: '#94A3B8' }}>
              Join thousands of investors who use ScopeTheMarket to stay ahead.
            </p>
            <Link
              to="/app"
              className="inline-flex items-center gap-2 px-8 py-4 text-base font-semibold rounded-xl transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.35)'
              }}
              data-testid="about-cta"
            >
              View Live Anomalies
            </Link>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-6" style={{ borderTop: '1px solid rgba(148, 163, 184, 0.1)' }}>
        <div className="max-w-6xl mx-auto">
          <p className="text-sm text-center" style={{ color: '#475569' }}>
            © {new Date().getFullYear()} ScopeTheMarket. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default About;
